<script lang="ts">
  // Spreadsheet import wizard: shared by CompaniesPage and PeoplePage's
  // "Import" button (see internal/api/handlers/import.go). Always creates
  // company+person pairs — there is no separate "import mode" per entity,
  // just two launch points into the same 4-step flow: pick file + vertical,
  // confirm the column mapping (pre-filled from the server's fuzzy-matched
  // suggestion), confirm the row count, then review the per-row report.
  import { createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { Upload, AlertTriangle, CheckCircle2 } from 'lucide-svelte';
  import { importApi } from '$lib/api/import';
  import { getErrorMessage } from '$lib/api/client';
  import { session } from '$lib/state/session.svelte';
  import { toast } from '$lib/state/toast.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import type { ImportCommitResult, ImportPreview } from '$lib/types';

  const { onClose }: { onClose: () => void } = $props();

  const queryClient = useQueryClient();

  const FIXED_TARGETS: { value: string; label: string }[] = [
    { value: 'skip', label: 'Skip this column' },
    { value: 'company_name', label: 'Company: Name' },
    { value: 'company_city', label: 'Company: City' },
    { value: 'company_pincode', label: 'Company: Pincode' },
    { value: 'company_phone', label: 'Company: Phone' },
    { value: 'company_email', label: 'Company: Email' },
    { value: 'company_website', label: 'Company: Website' },
    { value: 'person_name', label: 'Person: Name' },
    { value: 'person_email', label: 'Person: Email' },
    { value: 'person_phone', label: 'Person: Phone' },
    { value: 'person_city', label: 'Person: City' },
    { value: 'person_job_title', label: 'Person: Job title' }
  ];

  let step = $state<1 | 2 | 3 | 4>(1);

  // ===== Step 1: file + vertical =====
  let fileList = $state<FileList | null>(null);
  const file = $derived(fileList?.[0] ?? null);

  // Same explicit-vertical idiom as the email review-queue modals: always
  // visible, preselected only when unambiguous (single accessible vertical,
  // or an admin's current header selection), otherwise the user must pick
  // before continuing.
  function initialVerticalId(): number | undefined {
    const verticals = session.me?.verticals ?? [];
    if (verticals.length <= 1) return verticals[0]?.id;
    return session.selectedVertical !== 'all' ? session.selectedVertical : undefined;
  }
  let verticalId = $state<number | undefined>(initialVerticalId());
  function verticalName(id: number | undefined): string {
    if (id === undefined) return '—';
    return session.me?.verticals.find((v) => v.id === id)?.name ?? '—';
  }

  let uploadError = $state('');
  const previewMutation = createMutation(() => ({
    mutationFn: () => {
      if (!file) throw new Error('Choose a file first.');
      if (verticalId === undefined) throw new Error('Select a vertical first.');
      return importApi.preview(file, verticalId);
    },
    onSuccess: (data: ImportPreview) => {
      preview = data;
      mapping = [...data.suggested_mapping];
      uploadError = '';
      step = 2;
    },
    onError: (err: unknown) => (uploadError = getErrorMessage(err))
  }));

  // ===== Step 2: mapping =====
  let preview = $state<ImportPreview | null>(null);
  let mapping = $state<string[]>([]);

  const customCompanyOptions = $derived(
    session
      .customFieldsFor('companies', verticalId)
      .map((f) => ({ value: `custom:company:${f.key}`, label: `Company: ${f.label} (custom)` }))
  );
  const customPeopleOptions = $derived(
    session
      .customFieldsFor('people', verticalId)
      .map((f) => ({ value: `custom:people:${f.key}`, label: `Person: ${f.label} (custom)` }))
  );
  const targetOptions = $derived([...FIXED_TARGETS, ...customCompanyOptions, ...customPeopleOptions]);
  const hasCompanyNameMapped = $derived(mapping.includes('company_name'));

  // ===== Step 4: commit + results =====
  let commitError = $state('');
  let resultFilter = $state<'all' | 'errors' | 'warnings'>('all');
  const commitMutation = createMutation(() => ({
    mutationFn: () => {
      if (!preview) throw new Error('Nothing to import.');
      return importApi.commit(preview.token, mapping);
    },
    onSuccess: (data: ImportCommitResult) => {
      commitError = '';
      step = 4;
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: ['people'] });
      const t = data.totals;
      toast.success(
        `Import finished: ${t.companies_created} compan${t.companies_created === 1 ? 'y' : 'ies'} created, ` +
          `${t.people_created} people created${t.errors ? `, ${t.errors} row(s) had errors` : ''}.`
      );
    },
    onError: (err: unknown) => (commitError = getErrorMessage(err))
  }));

  const filteredResults = $derived(
    (commitMutation.data?.results ?? []).filter((r) => {
      if (resultFilter === 'errors') return r.company === 'error' || r.person === 'error';
      if (resultFilter === 'warnings') return r.warnings.length > 0;
      return true;
    })
  );

  function summaryLine(data: ImportCommitResult): string {
    const t = data.totals;
    const parts = [
      `${t.companies_created} compan${t.companies_created === 1 ? 'y' : 'ies'} created`,
      `${t.companies_matched} matched`,
      `${t.people_created} people created`,
      `${t.people_matched} matched`,
      `${t.people_skipped} skipped`
    ];
    let line = parts.join(', ') + '.';
    if (t.errors > 0) line += ` ${t.errors} row(s) had errors — see below.`;
    return line;
  }

  function statusClass(status: string): string {
    if (status === 'error') return 'text-red-600';
    if (status.startsWith('matched')) return 'text-dark-gray';
    if (status === 'skipped') return 'text-dark-gray';
    return 'text-toyama-teal';
  }
</script>

<Modal title="Import companies & people" {onClose} wide>
  {#if step === 1}
    <div class="space-y-4">
      <div>
        <label class="label" for="import-file">File (.csv or .xlsx)</label>
        <input id="import-file" type="file" accept=".csv,.xlsx" class="input" bind:files={fileList} />
      </div>
      <div>
        <label class="label" for="import-vertical">Vertical</label>
        {#if session.me && session.me.verticals.length > 1}
          <select id="import-vertical" class="input" bind:value={verticalId}>
            {#if verticalId === undefined}
              <option value={undefined} disabled selected>Select vertical&hellip;</option>
            {/if}
            {#each session.me.verticals as v (v.id)}<option value={v.id}>{v.name}</option>{/each}
          </select>
        {:else}
          <select id="import-vertical" class="input" disabled>
            <option>{session.me?.verticals[0]?.name ?? '—'}</option>
          </select>
        {/if}
      </div>
      {#if uploadError}<p class="text-sm text-red-600">{uploadError}</p>{/if}
      <div class="flex justify-end gap-2 pt-2">
        <button type="button" class="btn-secondary" onclick={onClose}>Cancel</button>
        <button
          type="button"
          class="btn-primary"
          disabled={!file || verticalId === undefined || previewMutation.isPending}
          onclick={() => previewMutation.mutate()}
        >
          <Upload class="h-4 w-4" />
          {previewMutation.isPending ? 'Reading file...' : 'Next'}
        </button>
      </div>
    </div>
  {:else if step === 2 && preview}
    <div class="space-y-4">
      <p class="text-[13px] text-dark-gray">
        {preview.row_count} data row{preview.row_count === 1 ? '' : 's'} found in <strong>{file?.name}</strong>. Confirm
        what each column maps to.
      </p>
      <div class="tym-table-wrap">
        <div class="tym-table-scroll max-h-[320px]">
          <table class="tym-table">
            <thead>
              <tr>
                {#each preview.headers as header, i (i)}
                  <th>{header || `Column ${i + 1}`}</th>
                {/each}
              </tr>
            </thead>
            <tbody>
              <tr>
                {#each preview.headers as _header, i (i)}
                  <td>
                    <select class="input !h-8 !py-1 !text-xs" bind:value={mapping[i]}>
                      {#each targetOptions as opt (opt.value)}
                        <option value={opt.value}>{opt.label}</option>
                      {/each}
                    </select>
                  </td>
                {/each}
              </tr>
              {#each preview.preview_rows as row, ri (ri)}
                <tr>
                  {#each row as cell, ci (ci)}
                    <td class="muted">{cell}</td>
                  {/each}
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>
      {#if !hasCompanyNameMapped}
        <p class="flex items-center gap-1.5 text-sm text-red-600">
          <AlertTriangle class="h-4 w-4" /> At least one column must be mapped to "Company: Name".
        </p>
      {/if}
      <div class="flex justify-end gap-2 pt-2">
        <button type="button" class="btn-secondary" onclick={() => (step = 1)}>Back</button>
        <button type="button" class="btn-primary" disabled={!hasCompanyNameMapped} onclick={() => (step = 3)}>
          Next
        </button>
      </div>
    </div>
  {:else if step === 3 && preview}
    <div class="space-y-4">
      <p class="text-[14px] text-navy">
        Import <strong>~{preview.row_count} rows</strong> as companies &amp; people in
        <strong>{verticalName(verticalId)}</strong>?
      </p>
      <p class="text-[13px] text-dark-gray">
        Existing companies (matched by name) and people (matched by email, or name + company) are reused rather than
        duplicated — see the report after import for exactly what happened per row.
      </p>
      {#if commitError}<p class="text-sm text-red-600">{commitError}</p>{/if}
      <div class="flex justify-end gap-2 pt-2">
        <button type="button" class="btn-secondary" onclick={() => (step = 2)}>Back</button>
        <button
          type="button"
          class="btn-primary"
          disabled={commitMutation.isPending}
          onclick={() => commitMutation.mutate()}
        >
          {commitMutation.isPending ? 'Importing...' : 'Import'}
        </button>
      </div>
    </div>
  {:else if step === 4 && commitMutation.data}
    <div class="space-y-4">
      <p class="flex items-center gap-1.5 text-[14px] text-navy">
        <CheckCircle2 class="h-4 w-4 text-toyama-teal" />
        {summaryLine(commitMutation.data)}
      </p>
      <div class="flex gap-2">
        {#each [
          { value: 'all', label: 'All' },
          { value: 'errors', label: 'Errors only' },
          { value: 'warnings', label: 'Warnings only' }
        ] as f (f.value)}
          <button
            type="button"
            class="tym-chip {resultFilter === f.value ? 'active' : ''}"
            onclick={() => (resultFilter = f.value as typeof resultFilter)}
          >
            {f.label}
          </button>
        {/each}
      </div>
      <div class="tym-table-wrap">
        <div class="tym-table-scroll max-h-[360px]">
          <table class="tym-table">
            <thead>
              <tr>
                <th>Row</th>
                <th>Company</th>
                <th>Person</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {#each filteredResults as r (r.row)}
                <tr>
                  <td class="muted">{r.row}</td>
                  <td class={statusClass(r.company)}>
                    {r.company}{#if r.company_name}<span class="block text-[11px] text-dark-gray">{r.company_name}</span
                      >{/if}
                  </td>
                  <td class={statusClass(r.person)}>
                    {r.person}{#if r.person_name}<span class="block text-[11px] text-dark-gray">{r.person_name}</span
                      >{/if}
                  </td>
                  <td class="text-[11.5px] text-dark-gray">
                    {r.error ?? ''}
                    {#each r.warnings as w (w)}<span class="block">{w}</span>{/each}
                  </td>
                </tr>
              {:else}
                <tr><td class="empty" colspan="4">No rows match this filter.</td></tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>
      <div class="flex justify-end pt-2">
        <button type="button" class="btn-primary" onclick={onClose}>Done</button>
      </div>
    </div>
  {/if}
</Modal>
